import React, { useState } from 'react';
import { MessageCircle, Plus, Minus, MapPin, Phone, Mail } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CONTACT } from '../data/products';

export default function OrderSection() {
  const { t, lang } = useLang();
  const o = t.order;
  const c = t.contact;

  const [qty, setQty] = useState(1);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');

  const total = 15 * qty;

  const handleOrder = () => {
    let msg;
    if (lang === 'ar') {
      msg  = `مرحباً Misky! 👋\n\n`;
      msg += `أود طلب المنتج التالي:\n\n`;
      msg += `📦 *كريم مسكي للوجه والجسم*\n`;
      msg += `   مركب السيراميد 2% · 150 مل\n`;
      msg += `   الكمية: ${qty}\n`;
      msg += `   السعر: 15 دينار × ${qty} = *${total} دينار*\n`;
      if (name) msg += `\n👤 الاسم: ${name}`;
      if (note) msg += `\n📝 ملاحظة: ${note}`;
      msg += `\n\nأرجو تأكيد الطلب، شكراً! ✨`;
    } else {
      msg  = `Hello Misky! 👋\n\n`;
      msg += `I'd like to order:\n\n`;
      msg += `📦 *MISKY Face & Body Cream*\n`;
      msg += `   Ceramide Complex 2% · 150 ml\n`;
      msg += `   Qty: ${qty}\n`;
      msg += `   Price: 15 JD × ${qty} = *${total} JD*\n`;
      if (name) msg += `\n👤 Name: ${name}`;
      if (note) msg += `\n📝 Note: ${note}`;
      msg += `\n\nPlease confirm my order. Thank you! ✨`;
    }
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <section id="order" className="py-24 bg-gradient-to-br from-misky-silver to-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-misky-mid text-xs uppercase mb-3">{o.sectionLabel}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-misky-black">{o.title}</h2>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-3xl border border-misky-silver shadow-sm p-8">
            {/* Product summary */}
            <div className="flex items-center gap-4 mb-8 p-4 bg-misky-white rounded-2xl border border-misky-silver">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center flex-shrink-0">
                <span className="font-display text-lg font-bold text-misky-black">M</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-misky-black text-sm">{o.productName}</div>
                <div className="text-xs text-misky-mid">{o.productSub}</div>
              </div>
              <div className="font-display text-xl font-bold text-misky-black flex-shrink-0">
                15 <span className="text-sm font-normal text-misky-mid">{t.hero.currency}</span>
              </div>
            </div>

            {/* Qty */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-misky-black mb-3">{o.quantityLabel}</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-misky-silver rounded-full overflow-hidden">
                  <button onClick={() => setQty(q => Math.max(1, q - 1))}
                    className="px-4 py-2.5 hover:bg-misky-silver transition-colors">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="px-5 font-bold text-lg text-misky-black">{qty}</span>
                  <button onClick={() => setQty(q => q + 1)}
                    className="px-4 py-2.5 hover:bg-misky-silver transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-misky-mid text-sm">
                  {o.total}: <span className="font-bold text-misky-black">{total} {t.hero.currency}</span>
                </div>
              </div>
            </div>

            {/* Name */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-misky-black mb-2">{o.nameLabel}</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={o.namePlaceholder}
                className="w-full border border-misky-silver rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-misky-black transition-colors"
              />
            </div>

            {/* Note */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-misky-black mb-2">{o.noteLabel}</label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={o.notePlaceholder}
                rows={3}
                className="w-full border border-misky-silver rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-misky-black transition-colors resize-none"
              />
            </div>

            <button
              onClick={handleOrder}
              className="wa-pulse w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1da851] text-white py-4 rounded-2xl font-bold text-base transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.01]"
            >
              <MessageCircle className="w-5 h-5" />
              {o.whatsappBtn}
            </button>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xl font-bold text-misky-black mb-5">{o.contactTitle}</h3>

            {[
              { icon: MessageCircle, label: c.whatsapp, value: `+${CONTACT.whatsapp}`, green: true, action: () => window.open(`https://wa.me/${CONTACT.whatsapp}`, '_blank') },
              { icon: Phone, label: c.phone, value: CONTACT.phone },
              { icon: Mail, label: c.email, value: CONTACT.email },
              { icon: MapPin, label: c.location, value: CONTACT.location },
            ].map(({ icon: Icon, label, value, green, action }) => (
              <div key={label}
                onClick={action}
                className={`flex items-center gap-3 p-4 rounded-2xl border border-misky-silver bg-white shadow-sm ${action ? 'cursor-pointer hover:shadow-md hover:border-misky-black/30 transition-all' : ''}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${green ? 'bg-[#25D366]/10' : 'bg-misky-black/5'}`}>
                  <Icon className={`w-5 h-5 ${green ? 'text-[#25D366]' : 'text-misky-gray'}`} />
                </div>
                <div>
                  <div className="text-xs text-misky-mid font-medium">{label}</div>
                  <div className="text-sm font-medium text-misky-black">{value}</div>
                </div>
              </div>
            ))}

            <div className="p-4 rounded-2xl bg-misky-black text-white">
              <p className="text-xs leading-relaxed text-misky-light">{o.noteBox}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
